import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ChevronDown, ChevronUp, Globe } from "lucide-react";
import SiteShell from "../components/SiteShell";
import Action from "../components/Action";
import {
  portfolioProjects,
  portfolioCategories,
  portfolioSummary,
} from "../data/portfolio";

/**
 * Portfolio — Green Gold Engineering track record.
 *
 * Structure:
 *   1. Hero (H1 + tagline + small kicker)
 *   2. Summary hero-stats band (4 big tiles)
 *   3. Secondary stats + countries popover
 *   4. Category filter chip bar
 *   5. Project grid (grouped by category when All is active)
 *   6. Closing NDA line + inline Action
 *
 * Data lives in /src/data/portfolio.js — swap-in ready when the client
 * dispatches the full 30+ project docx. Cards flagged `verified:false` are
 * scaffolds; they render with a small "awaiting details" note in the
 * expanded view.
 */

export default function Portfolio() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [countriesOpen, setCountriesOpen] = useState(false);

  const filtered = useMemo(() => {
    if (activeCategory === "all") return portfolioProjects;
    return portfolioProjects.filter((p) => p.category === activeCategory);
  }, [activeCategory]);

  // Group filtered projects by category (used when "All" is active).
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((p) => {
      if (!map[p.category]) map[p.category] = [];
      map[p.category].push(p);
    });
    return map;
  }, [filtered]);

  const activeCategoryMeta =
    activeCategory === "all"
      ? null
      : portfolioCategories.find((c) => c.id === activeCategory);

  return (
    <SiteShell>
      {/* ============ Hero ============ */}
      <section
        data-testid="portfolio-hero"
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "clamp(48px, 7vw, 96px) clamp(20px, 5vw, 60px) clamp(24px, 4vw, 40px)",
        }}
      >
        <div className="gg-eyebrow" style={{ marginBottom: 14 }}>
          Green Gold Engineering · 2024–2026
        </div>
        <h1
          className="gg-h1"
          data-testid="portfolio-h1"
          style={{
            fontFamily: "Fraunces, Georgia, serif",
            fontSize: "clamp(30px, 4.6vw, 56px)",
            lineHeight: 1.15,
            margin: 0,
          }}
        >
          Portfolio.
        </h1>
        <p
          data-testid="portfolio-tagline"
          style={{
            marginTop: 22,
            maxWidth: 640,
            color: "var(--text-cloud)",
            fontSize: "clamp(16px, 1.6vw, 20px)",
            lineHeight: 1.55,
            fontFamily: "Fraunces, Georgia, serif",
          }}
        >
          Fifty-plus projects. Thirty-one countries. From the Atacama to Svalbard.
        </p>
      </section>

      {/* ============ Hero stats band ============ */}
      <section
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "0 clamp(20px, 5vw, 60px)",
        }}
      >
        <div
          data-testid="portfolio-hero-stats"
          className="gg-portfolio-hero-stats"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 16,
          }}
        >
          <HeroStat
            testId="portfolio-stat-projects"
            value={portfolioSummary.total_projects}
            label="Projects executed"
          />
          <HeroStat
            testId="portfolio-stat-solar"
            value={portfolioSummary.solar_capacity_designed}
            label="Solar PV designed"
            caption={`(${portfolioSummary.solar_breakdown})`}
          />
          <HeroStat
            testId="portfolio-stat-bess"
            value={portfolioSummary.bess_capacity_designed}
            label="BESS capacity"
          />
          <HeroStat
            testId="portfolio-stat-countries"
            value={`${portfolioSummary.countries_served.length}`}
            label="Countries served"
          />
        </div>

        {/* Secondary stats row */}
        <div
          data-testid="portfolio-secondary-stats"
          style={{
            marginTop: 18,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 10,
          }}
        >
          <MetaRow label="Largest single project" value={portfolioSummary.largest_single_project} />
          <MetaRow label="Peak switchgear" value={portfolioSummary.switchgear_max_rating} />
          <MetaRow label="ABB BESS switchboards" value={portfolioSummary.bess_switchboards_abb} />
          <MetaRow label="Indian statutory approvals" value={portfolioSummary.indian_statutory_approvals} />
          <MetaRow label="Battery cell R&D" value={portfolioSummary.battery_cell_rd} />
          <MetaRow label="EV charging R&D" value={portfolioSummary.ev_charging_rd} />
          <MetaRow label="IoT products" value={portfolioSummary.iot_products_developed} />
        </div>

        {/* Countries popover */}
        <div style={{ marginTop: 22 }}>
          <button
            type="button"
            onClick={() => setCountriesOpen((o) => !o)}
            data-testid="portfolio-countries-toggle"
            style={{
              background: "transparent",
              border: `1px solid var(--line)`,
              borderRadius: 999,
              padding: "8px 16px",
              color: "var(--text-dim)",
              fontSize: 12.5,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Globe size={13} />
            {countriesOpen ? "Hide countries served" : `Show all ${portfolioSummary.countries_served.length} countries served`}
            {countriesOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          <AnimatePresence>
            {countriesOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                data-testid="portfolio-countries-list"
                style={{ overflow: "hidden" }}
              >
                <div
                  style={{
                    marginTop: 12,
                    padding: "16px 20px",
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid var(--line)",
                    borderRadius: 12,
                    color: "var(--text-cloud)",
                    fontSize: 13.5,
                    lineHeight: 1.9,
                    columnCount: 3,
                    columnGap: 24,
                  }}
                >
                  {portfolioSummary.countries_served.map((c) => (
                    <div key={c} style={{ breakInside: "avoid" }}>
                      · {c}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ============ Category filter ============ */}
      <section
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "clamp(48px, 6vw, 80px) clamp(20px, 5vw, 60px) 20px",
        }}
      >
        <div
          data-testid="portfolio-filter-bar"
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
          role="tablist"
          aria-label="Project category"
        >
          <FilterChip
            testId="portfolio-chip-all"
            active={activeCategory === "all"}
            onClick={() => { setActiveCategory("all"); setExpandedId(null); }}
            label="All"
            count={portfolioProjects.length}
          />
          {portfolioCategories.map((c) => {
            const count = portfolioProjects.filter((p) => p.category === c.id).length;
            return (
              <FilterChip
                key={c.id}
                testId={`portfolio-chip-${c.id}`}
                active={activeCategory === c.id}
                onClick={() => { setActiveCategory(c.id); setExpandedId(null); }}
                label={c.label}
                count={count}
              />
            );
          })}
        </div>
      </section>

      {/* ============ Project grid ============ */}
      <section
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "10px clamp(20px, 5vw, 60px) clamp(50px, 6vw, 80px)",
        }}
      >
        {activeCategory === "all" ? (
          // Grouped by category with section headers
          portfolioCategories.map((cat) => {
            const items = grouped[cat.id] || [];
            if (!items.length) return null;
            return (
              <CategorySection
                key={cat.id}
                category={cat}
                items={items}
                count={items.length}
                expandedId={expandedId}
                setExpandedId={setExpandedId}
              />
            );
          })
        ) : (
          // Single category — just one header + grid
          <>
            <SectionHeader category={activeCategoryMeta} count={filtered.length} />
            <ProjectGrid items={filtered} expandedId={expandedId} setExpandedId={setExpandedId} />
          </>
        )}
      </section>

      {/* ============ NDA closing line ============ */}
      <section
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "0 clamp(20px, 5vw, 60px) clamp(60px, 8vw, 100px)",
        }}
      >
        <p
          data-testid="portfolio-nda"
          style={{
            color: "var(--text-dim)",
            fontSize: 14.5,
            lineHeight: 1.7,
            textAlign: "center",
            maxWidth: 720,
            margin: "0 auto",
            paddingTop: 30,
            borderTop: "1px solid var(--line)",
          }}
        >
          Full technical documentation, SLDs, and case-study specifics are
          available upon request under NDA.{" "}
          <Action route="request-data-room" source="portfolio-nda">
            Request access
          </Action>
          .
        </p>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .gg-portfolio-hero-stats { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 480px) {
          .gg-portfolio-hero-stats { grid-template-columns: 1fr !important; }
          [data-testid="portfolio-countries-list"] > div { column-count: 2 !important; }
        }
      `}</style>
    </SiteShell>
  );
}

// ============ subcomponents ============

function HeroStat({ testId, value, label, caption }) {
  return (
    <div
      className="gg-glass"
      data-testid={testId}
      style={{ padding: "22px 22px 20px", display: "flex", flexDirection: "column" }}
    >
      <div
        style={{
          fontFamily: "Fraunces, Georgia, serif",
          fontSize: "clamp(30px, 3.4vw, 44px)",
          lineHeight: 1,
          color: "var(--gold-soft)",
        }}
      >
        {value}
      </div>
      <div
        style={{
          marginTop: 10,
          color: "var(--text-cloud)",
          fontSize: 13.5,
          letterSpacing: "0.02em",
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      {caption && (
        <div style={{ marginTop: 6, color: "var(--text-mute)", fontSize: 11.5, lineHeight: 1.5 }}>
          {caption}
        </div>
      )}
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div
      style={{
        padding: "12px 16px",
        background: "rgba(255,255,255,0.02)",
        border: "1px solid var(--line)",
        borderRadius: 10,
      }}
    >
      <div className="gg-eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ color: "var(--text-cloud)", fontSize: 13, lineHeight: 1.5 }}>
        {value}
      </div>
    </div>
  );
}

function FilterChip({ testId, active, onClick, label, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      role="tab"
      aria-selected={active}
      style={{
        padding: "9px 16px",
        borderRadius: 999,
        border: `1px solid ${active ? "rgba(244,169,31,0.5)" : "var(--line)"}`,
        background: active ? "rgba(244,169,31,0.10)" : "transparent",
        color: active ? "var(--gold-soft)" : "var(--text-dim)",
        fontSize: 13,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        transition: "background 0.2s, border-color 0.2s, color 0.2s",
      }}
    >
      {label}
      <span
        style={{
          color: active ? "var(--gold-soft)" : "var(--text-mute)",
          fontSize: 11,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {count}
      </span>
    </button>
  );
}

function SectionHeader({ category, count }) {
  if (!category) return null;
  return (
    <div
      data-testid={`portfolio-section-${category.id}`}
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 16,
        marginTop: 24,
        marginBottom: 18,
        paddingBottom: 12,
        borderBottom: "1px solid var(--line)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
        <span
          className="gg-eyebrow"
          style={{ color: "var(--gold-soft)", fontSize: 11 }}
        >
          {category.period}
        </span>
        <h2
          style={{
            fontFamily: "Fraunces, Georgia, serif",
            fontSize: "clamp(20px, 2.4vw, 28px)",
            margin: 0,
            color: "var(--paper)",
          }}
        >
          {category.label}
        </h2>
      </div>
      <span style={{ color: "var(--text-mute)", fontSize: 12 }}>
        {count} {count === 1 ? "project" : "projects"}
      </span>
    </div>
  );
}

function CategorySection({ category, items, count, expandedId, setExpandedId }) {
  return (
    <div>
      <SectionHeader category={category} count={count} />
      <ProjectGrid items={items} expandedId={expandedId} setExpandedId={setExpandedId} />
    </div>
  );
}

function ProjectGrid({ items, expandedId, setExpandedId }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        gap: 16,
        marginBottom: 40,
      }}
    >
      {items.map((p) => (
        <ProjectCard
          key={p.id}
          project={p}
          expanded={expandedId === p.id}
          onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
        />
      ))}
    </div>
  );
}

function ProjectCard({ project, expanded, onToggle }) {
  const visibleTags = expanded ? project.tags : project.tags.slice(0, 4);
  const hiddenTagCount = Math.max(0, project.tags.length - visibleTags.length);

  return (
    <motion.article
      layout
      whileHover={{ y: -3 }}
      transition={{ type: "spring", stiffness: 220, damping: 24 }}
      data-testid={`portfolio-card-${project.id}`}
      data-expanded={expanded ? "true" : "false"}
      data-verified={project.verified ? "true" : "false"}
      className="gg-glass gg-portfolio-card"
      style={{
        padding: 22,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
      }}
      onClick={onToggle}
    >
      {/* Top row: capacity + year */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div
          style={{
            fontFamily: "Fraunces, Georgia, serif",
            fontSize: 24,
            lineHeight: 1.1,
            color: "var(--gold-soft)",
            fontWeight: 500,
          }}
          data-testid={`portfolio-card-${project.id}-capacity`}
        >
          {project.capacity}
        </div>
        <span
          style={{
            fontSize: 11,
            letterSpacing: "0.12em",
            color: "var(--text-mute)",
            padding: "3px 8px",
            border: "1px solid var(--line)",
            borderRadius: 999,
          }}
        >
          {project.year}
        </span>
      </div>

      {/* Title */}
      <h3
        style={{
          fontFamily: "Fraunces, Georgia, serif",
          fontSize: 18,
          lineHeight: 1.3,
          margin: 0,
          color: "var(--paper)",
        }}
      >
        {project.title}
      </h3>

      {/* Location */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--text-dim)",
          fontSize: 12.5,
        }}
      >
        <MapPin size={12} color="var(--gold-soft)" />
        <span>{project.location}</span>
        {project.coordinates && (
          <span style={{ color: "var(--text-mute)", marginLeft: 4 }}>· {project.coordinates}</span>
        )}
      </div>

      {/* Description */}
      <p
        style={{
          color: "var(--text-cloud)",
          fontSize: 13.5,
          lineHeight: 1.6,
          margin: 0,
          display: expanded ? "block" : "-webkit-box",
          WebkitLineClamp: expanded ? "unset" : 2,
          WebkitBoxOrient: "vertical",
          overflow: expanded ? "visible" : "hidden",
        }}
      >
        {project.description}
      </p>

      {/* Expanded: key deliverables */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22 }}
            style={{ overflow: "hidden" }}
          >
            <div
              className="gg-eyebrow"
              style={{ marginTop: 4, marginBottom: 8, color: "var(--gold-soft)" }}
            >
              Key deliverables
            </div>
            <p style={{ color: "var(--text-cloud)", fontSize: 12.5, lineHeight: 1.65, margin: 0 }}>
              {project.key_deliverables}
            </p>
            {!project.verified && (
              <p
                style={{
                  marginTop: 10,
                  color: "var(--text-mute)",
                  fontSize: 11.5,
                  fontStyle: "italic",
                  borderTop: "1px dashed var(--line)",
                  paddingTop: 8,
                }}
              >
                Awaiting full docx from client for verified specifics.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tag chips */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 5,
          marginTop: 4,
        }}
      >
        {visibleTags.map((t) => (
          <span
            key={t}
            style={{
              padding: "3px 9px",
              borderRadius: 999,
              border: "1px solid var(--line)",
              color: "var(--text-dim)",
              fontSize: 10.5,
              letterSpacing: "0.02em",
            }}
          >
            {t}
          </span>
        ))}
        {!expanded && hiddenTagCount > 0 && (
          <span
            style={{
              padding: "3px 9px",
              borderRadius: 999,
              color: "var(--text-mute)",
              fontSize: 10.5,
            }}
          >
            +{hiddenTagCount} more
          </span>
        )}
      </div>

      {/* Expand affordance */}
      <div
        style={{
          marginTop: 4,
          color: "var(--text-mute)",
          fontSize: 11.5,
          display: "flex",
          alignItems: "center",
          gap: 4,
          alignSelf: "flex-start",
        }}
      >
        {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        {expanded ? "Collapse" : "Details"}
      </div>
    </motion.article>
  );
}
